import {
  ApolloClient,
  ApolloLink,
  FetchPolicy,
  InMemoryCache,
  WatchQueryFetchPolicy,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { captureException, captureMessage } from '@sentry/react'
import { createUploadLink } from 'apollo-upload-client'

import { CROSS_DOMAIN_STORAGE_URL, LOCAL_STORAGE_KEYS } from 'constants/static'
import { cacheTypePolicies } from 'graphql/cacheTypePolicies'
import { GET_LOCAL_CACHED_DATA } from 'graphql/queries'
import { resetCacheAndSetInitialValues } from 'utils/graphql'

import {
  SERVER_ERROR_MESSAGE,
  connectionLostMessage,
  createConnectionRetryLink,
  isConnectionError,
} from './connectionRetryLink'
import { GraphQLException } from './graphqlException'

const cache = new InMemoryCache({ typePolicies: cacheTypePolicies })

const authLink = setContext((_, { headers }) => {
  // Prefer the impersonation token when one is active so admin-on-admin
  // impersonation overlays the original session without overwriting it.
  const token =
    localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN_LOGIN) ||
    localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN)
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token ? `${token}` : '',
      // Identifies the admin console so the backend can scope the super-admin
      // IP-allowlist recovery exemption to admin requests only (the employee
      // app does not send this header, so it enforces the allowlist for all).
      'X-Tangelo-Client': 'admin',
    },
  }
})

const uploadLink = createUploadLink({
  uri: `${process.env.REACT_APP_GRAPHQL_BACKEND_URL}/graphql`,
  credentials: 'same-origin',
})

const defaultOptions = {
  watchQuery: {
    fetchPolicy: 'cache-and-network' as WatchQueryFetchPolicy,
  },
  query: {
    fetchPolicy: 'cache-and-network' as FetchPolicy,
  },
  mutate: {},
}

function createGraphqlClient() {
  const client = new ApolloClient({
    link: ApolloLink.from([
      // auth link
      authLink,
      // error link
      onError(({ graphQLErrors, networkError, operation }) => {
        // @ts-ignore
        if (networkError?.statusCode === 422) {
          return
        }
        if (graphQLErrors) {
          if (!process.env.CI) {
            // @ts-ignore
            if (networkError?.statusCode === 401) {
              captureMessage('Unauthorized', {
                level: 'info',
              })
            } else {
              const errors = graphQLErrors.map(
                (error) => new GraphQLException(error),
              )

              errors.forEach((error: GraphQLException) => {
                if (error.level !== 'WARNING') {
                  captureException(error, {
                    extra: {
                      query: {
                        body: operation?.query?.loc?.source.body,
                        variables: JSON.stringify(operation.variables),
                      },
                    },
                  })
                }
              })
            }
          }

          graphQLErrors.map(({ message, locations, path }) => {
            // client.writeData({ data: { hasError: message } })
            const data = client.readQuery({ query: GET_LOCAL_CACHED_DATA })
            client.writeQuery({
              query: GET_LOCAL_CACHED_DATA,
              data: { ...data, ...{ hasError: message } },
            })

            console.log(
              `[GraphQL error]: ${message}, Location: ${JSON.stringify(
                locations,
              )}, Path: ${path}`,
            )
          })
        }
        if (networkError) {
          const { response } = operation.getContext()
          if (!process.env.CI) {
            if (response?.status !== 401) {
              captureException(networkError, {
                extra: {
                  response,
                  query: {
                    body: operation?.query?.loc?.source.body,
                    variables: JSON.stringify(operation.variables),
                  },
                },
              })
            }
          }
          console.log(`[Network error]: ${networkError} ${response}`)
          if (response?.status === 401) {
            // If an impersonation token is active and it failed, drop just
            // that token so the original admin session takes over silently.
            const impersonationToken = localStorage.getItem(
              LOCAL_STORAGE_KEYS.TOKEN_LOGIN,
            )
            if (impersonationToken) {
              localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN_LOGIN)
              client.clearStore()
              window.location.href = '/dashboard'
              return
            }
            localStorage.clear()
            resetCacheAndSetInitialValues(client)
            const iframe = document.querySelector<HTMLIFrameElement>(
              '#cross-domain-iframe',
            )

            if (iframe) {
              iframe!.contentWindow!.postMessage(
                {
                  action: 'save',
                  key: LOCAL_STORAGE_KEYS.TOKEN,
                  value: '',
                },
                CROSS_DOMAIN_STORAGE_URL || '*',
              )
            }

            // client.writeData({ data: { isLoggedIn: false } })
            const data = client.readQuery({ query: GET_LOCAL_CACHED_DATA })
            client.writeQuery({
              query: GET_LOCAL_CACHED_DATA,
              data: { ...data, isLoggedIn: false },
            })
            window.location.href = '/'
          } else {
            // Post-auth IP allowlisting (CSC 3): a 403 with
            // { errors: [{ title: 'ip_not_allowed' }] } means the caller's
            // network is not on the client's allowlist — show a clear message.
            const serverError = networkError as any
            const isIpBlocked =
              response?.status === 403 &&
              serverError?.result?.errors?.[0]?.title === 'ip_not_allowed'
            let message = SERVER_ERROR_MESSAGE
            if (isIpBlocked) {
              message =
                "Access restricted: your current network isn't on your company's IP allowlist. Contact your administrator to be added."
            } else if (isConnectionError(networkError)) {
              // The connection retry link already retried it.
              message = connectionLostMessage(operation)
            }
            const data = client.readQuery({ query: GET_LOCAL_CACHED_DATA })
            client.writeQuery({
              query: GET_LOCAL_CACHED_DATA,
              data: { ...data, hasError: message },
            })
          }
        }
      }),
      // retries requests that got no response, before the error link sees them
      createConnectionRetryLink(),
      // http link
      uploadLink,
    ]),
    defaultOptions,
    cache,
    connectToDevTools: true,
  })

  return client
}

const client = createGraphqlClient()

export default client
